/**
 * MoralityStatusIndicator - Componente de indicador visual de moralidad tipo semáforo vertical
 * 
 * Este componente renderiza un semáforo vertical tradicional que indica el nivel de moralidad
 * de un candidato en evaluaciones psicológicas. Incluye luces de colores (verde, amarillo, rojo)
 * con una flecha indicadora que señala el nivel actual y un texto descriptivo.
 * 
 * Características principales:
 * - Semáforo vertical con 3 luces (verde=bajo riesgo, amarillo=intermedio, rojo=alto riesgo)
 * - Flecha indicadora que apunta al nivel actual de moralidad
 * - Solo una luz está activa según el nivel, las demás aparecen en gris
 * - Texto descriptivo personalizable que explica el resultado
 * - Diseño tradicional de semáforo con fondo negro y luces circulares
 * 
 * @author Ana Lizcano
 * @version 1.0.0
 */

import { useMemo } from "react";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

/**
 * Componente de indicador de moralidad con visualización tipo semáforo vertical
 * 
 * @param {string} level - Nivel de riesgo moral ("alto", "intermedio", "bajo")
 * @param {string} description - Texto descriptivo que explica la evaluación del candidato
 */
function MoralityStatusIndicator({ 
  level = "intermedio",
  description = "Comportamiento problemático que podría poner en riesgo la integridad de la empresa"
}) {
  /**
   * Determina los colores de las luces del semáforo según el nivel de riesgo
   * 
   * Sistema de colores:
   * - "alto": Solo luz roja activa (alto riesgo moral)
   * - "intermedio": Solo luz amarilla activa (riesgo moderado)
   * - "bajo": Solo luz verde activa (bajo riesgo moral)
   * - Las luces inactivas se muestran en gris (#e0e0e0)
   * 
   * @returns {Object} Objeto con colores para cada luz {red, yellow, green}
   */
  const getLightColors = useMemo(() => {
    switch(level) {
      case "alto":
        return { red: "#f44336", yellow: "#e0e0e0", green: "#e0e0e0" }; // Solo rojo activo
      case "intermedio":
        return { red: "#e0e0e0", yellow: "#ff9800", green: "#e0e0e0" }; // Solo amarillo activo
      case "bajo":
        return { red: "#e0e0e0", yellow: "#e0e0e0", green: "#4caf50" }; // Solo verde activo
      default:
        return { red: "#e0e0e0", yellow: "#ff9800", green: "#e0e0e0" }; // Por defecto: intermedio
    }
  }, [level]);

  /**
   * Calcula la posición vertical de la flecha indicadora
   * 
   * Posiciones calculadas desde la parte superior del semáforo:
   * - "bajo": 20px (apunta a la luz verde superior)
   * - "intermedio": 68px (apunta a la luz amarilla central)
   * - "alto": 116px (apunta a la luz roja inferior)
   * 
   * @returns {string} Posición CSS en píxeles para la propiedad 'top'
   */
  const getArrowPosition = useMemo(() => {
    switch(level) {
      case "bajo":
        return '20px'; // Flecha apunta a luz verde
      case "intermedio":
        return '68px'; // Flecha apunta a luz amarilla
      case "alto":
        return '116px'; // Flecha apunta a luz roja
      default:
        return '68px'; // Por defecto: intermedio
    }
  }, [level]);

  return (
    <MDBox sx={{
      backgroundColor: '#e8e8e8',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '30px'
    }}>


      {/* Contenedor del semáforo y texto descriptivo */}
      <MDBox sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '40px',
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px'
      }}>
        {/* Estructura del semáforo vertical con fondo negro */}
        <MDBox sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#333',
          borderRadius: '25px',
          padding: '8px',
          gap: '8px',
          position: 'relative'
        }}>
          {/* Luz verde (superior) - activa cuando level="bajo" */}
          <MDBox sx={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: getLightColors.green,
            border: '2px solid #666'
          }} />
          
          {/* Luz amarilla (central) - activa cuando level="intermedio" */}
          <MDBox sx={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: getLightColors.yellow,
            border: '2px solid #666'
          }} />
          
          {/* Luz roja (inferior) - activa cuando level="alto" */}
          <MDBox sx={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: getLightColors.red,
            border: '2px solid #666'
          }} />
          
          {/* Flecha triangular que apunta a la luz activa */}
          <MDBox sx={{
            position: 'absolute',
            right: '-25px', // Posicionada a la derecha del semáforo
            top: getArrowPosition, // Posición vertical calculada según el nivel
            width: '0',
            height: '0',
            // Triángulo CSS usando bordes transparentes
            borderTop: '10px solid transparent',
            borderBottom: '10px solid transparent',
            borderLeft: '15px solid #333' // Lado visible del triángulo
          }} />
        </MDBox>

        {/* Área de texto explicativo del resultado */}
        <MDBox sx={{
          flex: 1,
          maxWidth: '400px'
        }}>
          <MDTypography 
            variant="body1" 
            sx={{
              color: '#555',
              lineHeight: 1.6
            }}
          >
            {description}
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

/**
 * Definición de PropTypes para validación de props del componente
 */
MoralityStatusIndicator.propTypes = {
  level: PropTypes.oneOf(["alto", "intermedio", "bajo"]),
  description: PropTypes.string
};

export default MoralityStatusIndicator;